/**
 * Módulo para gerenciamento de seleção de tenant baseado em querystring e localStorage
 */

// Constantes
const QUERY_PARAM_NAME = 'tenant';
const REDIRECT_TO_PARAM_NAME = 'redirectTo';
const LOCAL_STORAGE_KEY = 'selectedTenant';
const REALM_STORAGE_KEY = 'realm';

// Interfaces
interface AvailableTenants {
  [hostname: string]: string[];
}

interface TenantSelectionOptions {
  availableTenants: AvailableTenants;
  tenantSelectionUrl: string;
}

interface TenantSelectionResult {
  tenant: string;
  realm: string;
}

/**
 * Inicializa a seleção de tenant baseado em querystring ou localStorage
 *
 * @param options - Configurações contendo availableTenants e tenantSelectionUrl
 * @param saveRealm - Salva realm no localStorage (use false para quando a tela de selecao de realm esteja ativa)
 * @returns Objeto contendo tenant e realm válidos selecionados ou null se houve redirecionamento
 */
export function initializeTenantSelection(
  options: TenantSelectionOptions,
  saveRealm: boolean = true,
): TenantSelectionResult | null {
  const { availableTenants, tenantSelectionUrl } = options;

  /**
   * Extrai o valor do parâmetro tenant da querystring
   */
  function getTenantFromQuery(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    const tenant = urlParams.get(QUERY_PARAM_NAME);
    return tenant ? tenant.trim() : null;
  }

  /**
   * Extrai o valor do parâmetro redirectTo da querystring
   */
  function getRedirectToFromQuery(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectTo = urlParams.get(REDIRECT_TO_PARAM_NAME);
    return redirectTo ? redirectTo.trim() : null;
  }

  // Função auxiliar para obter hostname atual
  function getCurrentHostname(): string {
    try {
      const url = new URL(window.location.href);
      return url.hostname;
    } catch {
      return window.location.hostname;
    }
  }

  /**
   * Navega para a URL de redirecionamento sem alterar o histórico de navegação
   * Valida se a URL é segura (mesmo origin ou relativa)
   * Remove os parâmetros tenant e lang da URL de destino para evitar conflitos
   */
  function navigateToRedirectUrl(redirectUrl: string): void {
    try {
      // Validar se é URL segura (mesmo origin ou relativa)
      const url = new URL(redirectUrl, window.location.origin);
      if (url.origin !== window.location.origin) {
        console.warn(
          'Redirecionamento para origem externa bloqueado:',
          redirectUrl,
        );
        return;
      }

      // Remover parâmetros tenant e lang da URL de destino para evitar conflitos
      url.searchParams.delete(QUERY_PARAM_NAME);
      url.searchParams.delete('lang');

      // Navegar usando replaceState (sem alterar histórico)
      window.history.replaceState({}, document.title, url.toString());
    } catch (error) {
      console.warn('Erro ao processar URL de redirecionamento:', error);
    }
  }

  /**
   * Recupera o tenant salvo no localStorage
   */
  function getTenantFromLocalStorage(): string | null {
    try {
      return localStorage.getItem(LOCAL_STORAGE_KEY);
    } catch (error) {
      console.warn('Erro ao acessar localStorage:', error);
      return null;
    }
  }

  /**
   * Verifica se o hostname atual possui apenas um tenant disponível
   */
  function hasOnlyOneTenant(availableTenants: AvailableTenants): string | null {
    // Tentar obter hostname de window.location.href para contornar limitações do mock
    const currentHostname = getCurrentHostname();

    const validTenants = availableTenants[currentHostname];

    if (
      validTenants &&
      Array.isArray(validTenants) &&
      validTenants.length === 1
    ) {
      return validTenants[0];
    }

    return null;
  }

  /**
   * Valida se o tenant é válido para o hostname atual
   */
  function isValidTenant(
    tenant: string,
    availableTenants: AvailableTenants,
  ): boolean {
    // Tentar obter hostname de window.location.href para contornar limitações do mock
    let currentHostname: string;
    try {
      const url = new URL(window.location.href);
      currentHostname = url.hostname;
    } catch {
      currentHostname = window.location.hostname;
    }

    const validTenants = availableTenants[currentHostname];

    if (!validTenants || !Array.isArray(validTenants)) {
      return false;
    }

    return validTenants.includes(tenant);
  }

  /**
   * Salva o tenant no localStorage
   */
  function saveTenantToLocalStorage(
    tenant: string,
    saveRealm: boolean = true,
  ): void {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, tenant);
      // Salvar flag realm baseada nas regras de negócio
      if (saveRealm) {
        saveRealmFlag(tenant);
      }
    } catch (error) {
      console.warn('Erro ao salvar no localStorage:', error);
    }
  }

  /**
   * Determina o valor do realm baseado no tenant e hostname
   */
  function getRealm(tenant: string, hostname: string): string {
    // Verificar exceções específicas de hostname
    if (hostname === 'cora.ambev.com.br') {
      return 'abi';
    } else if (hostname === 'cora-revenda.ambev.com.br') {
      return 'third';
    } else if (hostname === 'cora-revendas-stg.ambevdevs.com.br') {
      return 'third';
    } else if (tenant.endsWith('-3pd')) {
      // Aplicar regra padrão baseada no sufixo do tenant
      return 'third';
    }
    return 'abi';
  }

  /**
   * Salva a flag "realm" no localStorage baseada no nome do tenant e hostname
   */
  function saveRealmFlag(tenant: string): void {
    try {
      // Obter hostname atual
      const currentHostname = getCurrentHostname();

      const realmValue = getRealm(tenant, currentHostname);
      localStorage.setItem(REALM_STORAGE_KEY, realmValue);
    } catch (error) {
      console.warn('Erro ao salvar flag realm no localStorage:', error);
    }
  }

  /**
   * Remove os parâmetros tenant e lang da URL sem alterar o histórico de navegação
   */
  function removeQueryFromUrl(): void {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete(QUERY_PARAM_NAME);
    urlParams.delete('lang');

    const url = new URL(window.location.href);
    const queryString = urlParams.toString();
    const newUrl = `${url.origin}${url.pathname}${
      queryString ? '?' + queryString : ''
    }${url.hash}`;

    // Usa replaceState para não alterar o histórico
    window.history.replaceState({}, document.title, newUrl);
  }

  /**
   * Redireciona o usuário para a URL de seleção de tenant
   * Adiciona um parâmetro redirectTo com o path + querystring + hash da URL atual
   */
  function redirectToTenantSelection(url: string): void {
    const currentUrl = new URL(window.location.href);

    // Criar o valor do redirectTo com pathname + search + hash
    const redirectToValue =
      currentUrl.pathname + currentUrl.search + currentUrl.hash;

    // Se a URL é absoluta, criar nova URL e adicionar apenas o parâmetro redirectTo
    if (url.startsWith('http')) {
      const tenantSelectionUrl = new URL(url);
      tenantSelectionUrl.searchParams.set('redirectTo', redirectToValue);
      window.location.href = tenantSelectionUrl.toString();
      return;
    }

    // Para URLs relativas, usar URL constructor e adicionar apenas o parâmetro redirectTo
    const redirectUrl = new URL(url, window.location.origin);
    redirectUrl.searchParams.set('redirectTo', redirectToValue);

    window.location.href = redirectUrl.toString();
  }

  // 0. VERIFICAR SE EXISTE PARÂMETRO redirectTo PARA NAVEGAÇÃO DE RETORNO
  const queryTenant = getTenantFromQuery();
  const redirectTo = getRedirectToFromQuery();
  if (redirectTo) {
    // Navegar para a URL de redirecionamento sem alterar o histórico
    navigateToRedirectUrl(redirectTo);
    // Continuar com a lógica normal após a navegação
  }

  // 1. NOVA REGRA: Se hostname possui apenas um tenant, retornar automaticamente
  const singleTenant = hasOnlyOneTenant(availableTenants);
  if (singleTenant) {
    // Salvar flag realm para tenant único
    if (saveRealm) {
      saveRealmFlag(singleTenant);
    }
    // Remover parâmetro tenant da URL se existir
    removeQueryFromUrl();
    // Retornar objeto com tenant e realm
    const currentHostname = getCurrentHostname();
    return {
      tenant: singleTenant,
      realm: getRealm(singleTenant, currentHostname),
    };
  }

  // 2. Verificar se existe querystring com tenant

  if (queryTenant) {
    // Validar tenant da querystring
    if (isValidTenant(queryTenant, availableTenants)) {
      // Tenant válido: salvar no localStorage e remover querystring
      saveTenantToLocalStorage(queryTenant, saveRealm);
      removeQueryFromUrl();
      // Retornar objeto com tenant e realm
      const currentHostname = getCurrentHostname();
      return {
        tenant: queryTenant,
        realm: getRealm(queryTenant, currentHostname),
      };
    } else {
      // Tenant inválido: redirecionar para seleção
      redirectToTenantSelection(tenantSelectionUrl);
      return null;
    }
  }

  // 3. Não há querystring: verificar localStorage
  const storedTenant = getTenantFromLocalStorage();

  if (storedTenant) {
    // Validar tenant do localStorage
    if (isValidTenant(storedTenant, availableTenants)) {
      // Tenant válido: atualizar flag realm e retornar
      if (saveRealm) {
        saveRealmFlag(storedTenant);
      }
      // Retornar objeto com tenant e realm
      const currentHostname = getCurrentHostname();
      return {
        tenant: storedTenant,
        realm: getRealm(storedTenant, currentHostname),
      };
    } else {
      // Tenant inválido: limpar localStorage e redirecionar
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      redirectToTenantSelection(tenantSelectionUrl);
      return null;
    }
  }

  // 4. Não há querystring nem localStorage: redirecionar para seleção
  redirectToTenantSelection(tenantSelectionUrl);
  return null;
}

/**
 * Remove o tenant do localStorage e redireciona para a URL de seleção de tenant
 * @param tenantSelectionUrl URL para a página de seleção de tenant
 */
export function clearTenantAndRedirect(tenantSelectionUrl: string): void {
  try {
    // Remover tenant do localStorage
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  } catch (error) {
    console.warn('Erro ao limpar tenant e redirecionar:', error);
  }

  // Redirecionar para a URL de seleção de tenant (sempre executar, mesmo se houver erro no localStorage)
  try {
    const currentUrl = new URL(window.location.href);

    // Criar o valor do redirectTo com pathname + search + hash
    const redirectToValue =
      currentUrl.pathname + currentUrl.search + currentUrl.hash;

    // Se a URL é absoluta, criar nova URL e adicionar apenas o parâmetro redirectTo
    if (tenantSelectionUrl.startsWith('http')) {
      const selectionUrl = new URL(tenantSelectionUrl);
      selectionUrl.searchParams.set('redirectTo', redirectToValue);
      window.location.href = selectionUrl.toString();
      return;
    }

    // Para URLs relativas, usar URL constructor e adicionar apenas o parâmetro redirectTo
    const redirectUrl = new URL(tenantSelectionUrl, window.location.origin);
    redirectUrl.searchParams.set('redirectTo', redirectToValue);

    window.location.href = redirectUrl.toString();
  } catch (error) {
    console.warn('Erro ao redirecionar:', error);
  }
}

// Exportar tipos para uso externo
export type { AvailableTenants, TenantSelectionOptions, TenantSelectionResult };
